import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  type ChatModelAdapter,
  type FileMessagePart as FileContentPart,
  type ReasoningMessagePart as ReasoningContentPart,
  type SourceMessagePart as SourceContentPart,
  type TextMessagePart as TextContentPart,
  type ThreadMessage,
  type ToolCallMessagePart as ToolCallContentPart,
} from "@assistant-ui/react";
import type { ReadonlyJSONObject } from "./utils/jsonTypes";
import { type ReactNode } from "react";
import { ClientProvider } from "./ClientContext";
import { FrontPosition } from "./event";
import { processStreamEvent } from "./utils";
import { prepareRequestData, sendBackendRequest } from "./utils/requestManager";
import { findParentId } from "./utils/threadManagement";
import { extractConversationId } from "./utils/urlManagement";

export type TToolCallBackendData<TData = unknown> = {
  backendToolName: string;
  isFirstTool: boolean;
  front_position?: FrontPosition;
  data: TData | { error: string } | null;
  error?: string;
};

export type ThreadAssistantContentPart =
  | TextContentPart
  | ReasoningContentPart
  | ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>
  | SourceContentPart
  | FileContentPart;

/**
 * Type for message metadata in our application
 */
export type MessageMetadata = {
  custom: {
    backend_id: string | null;
    parent_id: string | null;
    [key: string]: any;
  };
};

/**
 * Backend API request parameters interface
 */
interface BackendApiParams {
  messages: readonly ThreadMessage[];
  abortSignal?: AbortSignal;
  parent_id: string | null;
  client: string;
}

/**
 * Handles communication with the backend API
 * @param params - Backend API parameters
 * @returns An async iterator for processing the stream
 */
async function backendApi({
  messages,
  abortSignal,
  parent_id,
  client,
}: BackendApiParams) {
  const conversationId = extractConversationId();
  const requestData = prepareRequestData(messages, conversationId, parent_id);

  return sendBackendRequest(requestData, client, abortSignal, conversationId);
}

/**
 * Creates a model adapter with the specified client
 * @param client - The client identifier
 * @returns Chat model adapter configured for the client
 */
function createModelAdapter(client: string): ChatModelAdapter {
  return {
    async *run({ messages, abortSignal }) {
      const parent_id = findParentId(messages);

      const stream = await backendApi({
        messages,
        abortSignal,
        parent_id,
        client,
      });

      let content: ThreadAssistantContentPart[] = [];
      let currentText = "";
      let toolCalls: Record<
        string,
        ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>
      > = {};
      let backend_id: number | null = null;

      for await (const event of stream) {
        const {
          content: newContent,
          currentText: newText,
          toolCalls: newToolCalls,
          backend_id: newBackendId,
        } = processStreamEvent(
          event,
          content,
          currentText,
          toolCalls,
          backend_id,
        );

        content = newContent;
        currentText = newText;
        toolCalls = newToolCalls;
        backend_id = newBackendId;

        yield {
          content,
          metadata: {
            custom: {
              backend_id,
              parent_id,
            },
          },
        };
      }
    },
  };
}

/**
 * Custom runtime provider that uses our backend API
 * @param client - The client identifier
 * @param children - React child components
 * @returns AssistantRuntimeProvider with custom runtime and client context
 */
export function RuntimeProvider({
  client,
  children,
  path_route,
}: Readonly<{
  client: string;
  children: ReactNode;
  path_route?: string;
}>) {
  const modelAdapter = createModelAdapter(client);
  const runtime = useLocalRuntime(modelAdapter);

  return (
    <ClientProvider client={client} path_route={path_route || client}>
      <AssistantRuntimeProvider runtime={runtime}>
        {children}
      </AssistantRuntimeProvider>
    </ClientProvider>
  );
}
